/**
 * PLC 代码编辑器工具栏
 * 
 * 功能：
 * - 语言切换下拉菜单
 * - 代码操作按钮（格式化、复制、下载等）
 * - 状态指示器（已保存/已修改/加载中）
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { PlcLanguageType, PlcLanguageInfo } from '../../types';
import { PLC_LANGUAGES } from '../../types';

// ============================================
// 导出格式定义
// ============================================

/** 导出格式标识 */
export type ExportFormatId = 'ab-st' | 'siemens-scl' | 'siemens-awl';

/** 导出格式信息 */
export interface ExportFormat {
  readonly id: ExportFormatId;
  readonly name: string;
  readonly description: string;
  readonly extension: string;
  /** 换行符类型：'lf' = Unix, 'crlf' = Windows */
  readonly lineEnding: 'lf' | 'crlf';
  readonly vendor: string;
}

/** 所有支持的导出格式 */
export const EXPORT_FORMATS: readonly ExportFormat[] = [
  {
    id: 'ab-st',
    name: 'AB ST',
    description: 'Allen-Bradley 结构化文本',
    extension: '.st',
    lineEnding: 'crlf',
    vendor: 'Allen-Bradley'
  },
  {
    id: 'siemens-scl',
    name: 'Siemens SCL',
    description: '西门子 SCL (可导入 TIA Portal)',
    extension: '.scl',
    lineEnding: 'crlf',
    vendor: 'Siemens'
  },
  {
    id: 'siemens-awl',
    name: 'Siemens STL',
    description: '西门子 STL 指令表',
    extension: '.awl',
    lineEnding: 'crlf',
    vendor: 'Siemens'
  }
];

// ============================================
// 类型定义
// ============================================

export interface PlcEditorToolbarProps {
  /** 当前语言 */
  readonly language: PlcLanguageType;
  /** 语言变更回调 */
  readonly onLanguageChange?: (language: PlcLanguageType) => void;
  /** 是否有未保存修改 */
  readonly isDirty?: boolean;
  /** 是否正在加载 */
  readonly isLoading?: boolean;
  /** 格式化代码回调 */
  readonly onFormat?: () => void;
  /** 复制代码回调 */
  readonly onCopy?: () => void;
  /** 导出代码回调，传入选中的导出格式 */
  readonly onDownload?: (format: ExportFormat) => void;
  /** 撤销回调 */
  readonly onUndo?: () => void;
  /** 重做回调 */
  readonly onRedo?: () => void;
  /** 发送代码到 AI 回调 */
  readonly onSendToAI?: () => void;
  /** 打开测试面板回调 */
  readonly onOpenTest?: () => void;
  /** 自定义类名 */
  readonly className?: string;
}

// ============================================
// 子组件：语言选择器
// ============================================

interface LanguageSelectorProps {
  readonly language: PlcLanguageType;
  readonly onSelect: (language: PlcLanguageType) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  language,
  onSelect
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 当前选中的语言信息
  const currentLang: PlcLanguageInfo | undefined = PLC_LANGUAGES.find(
    lang => lang.id === language
  );
  
  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  const handleToggle = useCallback((): void => {
    setIsOpen(prev => !prev);
  }, []);
  
  const handleSelect = useCallback(
    (langId: PlcLanguageType): void => {
      onSelect(langId);
      setIsOpen(false);
    },
    [onSelect]
  );
  
  return (
    <div className="plc-language-selector" ref={containerRef}>
      <button
        type="button"
        className="plc-language-trigger"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{currentLang?.name ?? 'ST'}</span>
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} />
      </button>
      
      {isOpen && (
        <div className="plc-language-menu" role="listbox">
          {PLC_LANGUAGES.map(lang => (
            <div
              key={lang.id}
              className={`plc-language-item ${lang.id === language ? 'active' : ''}`}
              role="option"
              aria-selected={lang.id === language}
              onClick={() => handleSelect(lang.id)}
            >
              <div className="plc-language-item-info">
                <span className="plc-language-item-name">{lang.name}</span>
                <span className="plc-language-item-desc">{lang.description}</span>
              </div>
              {lang.id === language && (
                <i className="fa fa-check plc-language-item-check" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// 子组件：导出格式菜单
// ============================================

interface ExportFormatMenuProps {
  readonly onSelect: (format: ExportFormat) => void;
  readonly disabled?: boolean;
}

const ExportFormatMenu: React.FC<ExportFormatMenuProps> = ({
  onSelect,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = useCallback((): void => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (format: ExportFormat): void => {
      onSelect(format);
      setIsOpen(false);
    },
    [onSelect]
  );

  // 按厂商分组
  const groupedFormats = EXPORT_FORMATS.reduce<Record<string, ExportFormat[]>>(
    (acc, fmt) => {
      const group = acc[fmt.vendor] ?? [];
      return { ...acc, [fmt.vendor]: [...group, fmt] };
    },
    {}
  );

  return (
    <div className="plc-export-menu-container" ref={containerRef}>
      <button
        type="button"
        className="plc-toolbar-btn"
        title="导出代码"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="导出代码"
      >
        <i className="fa fa-download" />
      </button>

      {isOpen && (
        <div className="plc-export-menu" role="listbox">
          <div className="plc-export-menu-title">选择导出格式</div>
          {Object.entries(groupedFormats).map(([vendor, formats]) => (
            <div key={vendor} className="plc-export-group">
              <div className="plc-export-group-label">{vendor}</div>
              {formats.map(fmt => (
                <div
                  key={fmt.id}
                  className="plc-export-item"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleSelect(fmt)}
                >
                  <div className="plc-export-item-info">
                    <span className="plc-export-item-name">
                      {fmt.name}
                      <span className="plc-export-item-ext">{fmt.extension}</span>
                    </span>
                    <span className="plc-export-item-desc">{fmt.description}</span>
                  </div>
                  {fmt.lineEnding === 'crlf' && (
                    <span className="plc-export-item-badge">CRLF</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// 子组件：状态指示器
// ============================================

interface StatusIndicatorProps {
  readonly isDirty: boolean;
  readonly isLoading: boolean;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  isDirty,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="plc-status-indicator loading">
        <span className="plc-status-dot" />
        <span>AI 生成中...</span>
      </div>
    );
  }
  
  if (isDirty) {
    return (
      <div className="plc-status-indicator modified">
        <span className="plc-status-dot" />
        <span>已修改</span>
      </div>
    );
  }
  
  return (
    <div className="plc-status-indicator">
      <span className="plc-status-dot" />
      <span>已保存</span>
    </div>
  );
};

// ============================================
// 子组件：工具栏按钮
// ============================================

interface ToolbarButtonProps {
  readonly icon: string;
  readonly title: string;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly primary?: boolean;
  readonly label?: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  icon,
  title,
  onClick,
  disabled = false,
  primary = false,
  label
}) => {
  const buttonClass = [
    'plc-toolbar-btn',
    primary ? 'primary' : '',
    label ? 'plc-toolbar-btn-text' : ''
  ]
    .filter(Boolean)
    .join(' ');
  
  return (
    <button
      type="button"
      className={buttonClass}
      title={title}
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
    >
      <i className={`fa fa-${icon}`} />
      {label && <span>{label}</span>}
    </button>
  );
};

// ============================================
// 主组件：工具栏
// ============================================

export const PlcEditorToolbar: React.FC<PlcEditorToolbarProps> = ({
  language,
  onLanguageChange,
  isDirty = false,
  isLoading = false,
  onFormat,
  onCopy,
  onDownload,
  onUndo,
  onRedo,
  onSendToAI,
  onOpenTest,
  className = ''
}) => {
  // 语言变更处理
  const handleLanguageChange = useCallback(
    (newLang: PlcLanguageType): void => {
      onLanguageChange?.(newLang);
    },
    [onLanguageChange]
  );
  
  // 复制成功提示状态
  const [copySuccess, setCopySuccess] = useState(false);
  
  // 复制处理
  const handleCopy = useCallback((): void => {
    onCopy?.();
    setCopySuccess(true);
    
    // 2秒后重置状态
    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  }, [onCopy]);
  
  // 判断是否为图形化语言
  const isGraphical = language === 'lad' || language === 'fbd';
  
  return (
    <div className={`plc-editor-toolbar ${className}`}>
      {/* 左侧区域 */}
      <div className="plc-toolbar-left">
        <LanguageSelector
          language={language}
          onSelect={handleLanguageChange}
        />
        
        {/* <StatusIndicator isDirty={isDirty} isLoading={isLoading} /> */}
      </div>
      
      {/* 右侧区域 */}
      <div className="plc-toolbar-right">
        {/* 撤销/重做 */}
        <ToolbarButton
          icon="undo"
          title="撤销 (Ctrl+Z)"
          onClick={onUndo}
          disabled={isLoading}
        />
        <ToolbarButton
          icon="redo"
          title="重做 (Ctrl+Y)"
          onClick={onRedo}
          disabled={isLoading}
        />
        
        {/* 分隔符 */}
        <div style={{ width: 1, height: 20, background: 'var(--plc-border-color)', margin: '0 4px' }} />
        
        {/* 格式化（仅文本语言） */}
        {!isGraphical && (
          <ToolbarButton
            icon="align-left"
            title="格式化代码 (Ctrl+Shift+F)"
            onClick={onFormat}
            disabled={isLoading}
          />
        )}
        
        {/* 复制 */}
        <ToolbarButton
          icon={copySuccess ? 'check' : 'copy'}
          title={copySuccess ? '已复制!' : '复制代码'}
          onClick={handleCopy}
          disabled={isLoading}
        />
        
        {/* 导出 */}
        <ExportFormatMenu
          onSelect={(format) => onDownload?.(format)}
          disabled={isLoading}
        />
        
        {/* 分隔符 */}
        <div style={{ width: 1, height: 20, background: 'var(--plc-border-color)', margin: '0 4px' }} />
        
        {/* 仿真测试按钮 */}
        <ToolbarButton
          icon="flask"
          title="仿真测试"
          label="测试"
          onClick={onOpenTest}
          disabled={isLoading}
          primary
        />
        
        {/* 发送到 AI */}
        {/* <ToolbarButton
          icon="paper-plane"
          title="发送代码给 AI 优化"
          label="让 AI 优化"
          onClick={onSendToAI}
          disabled={isLoading}
          primary
        /> */}
      </div>
    </div>
  );
};

export default PlcEditorToolbar;